import React, { useState } from 'react';
import { restaurantInfo } from '../data/mockData';
import { Phone, MapPin, Clock, Facebook, Instagram, X } from 'lucide-react';

const LegalModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/80"></div>
      
      {/* Modal content */}
      <div 
        className="relative bg-[#1A1A1A] rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          aria-label="Fermer"
        >
          <X size={24} />
        </button>

        {/* Content */}
        <div className="p-8">
          <h2 
            className="text-3xl font-bold text-[#D4A85F] mb-6 text-center"
            style={{ fontFamily: "'Crimson Text', serif" }}
          >
            Mentions légales
          </h2>
          
          <div className="space-y-4 text-gray-300" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <p className="text-xl font-semibold text-white">PLANETE FOOD Massy</p>
            <p><span className="text-[#D4A85F]">Adresse :</span> 58 Av. Raymond Aron, 91300 Massy</p>
            <p><span className="text-[#D4A85F]">Téléphone :</span> 01 69 30 78 53</p>
            <div className="border-t border-gray-700 pt-4 mt-6">
              <p className="text-sm text-gray-400 leading-relaxed">
                Site développé indépendamment pour PLANETE FOOD Massy.<br />
                Tous droits réservés.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);

  return (
    <footer className="bg-black text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Logo & Description */}
          <div>
            <img
              src="/planete-food-logo.png"
              alt="PLANETE FOOD Logo"
              className="h-44 md:h-52 w-auto mb-4"
            />
            <p 
              className="text-gray-400 leading-relaxed"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {restaurantInfo.tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 
              className="text-[#D4A85F] font-bold text-lg mb-4"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Liens Rapides
            </h3>
            <ul className="space-y-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
              <li>
                <a href="#hero" className="text-gray-400 hover:text-[#3ABEFF] transition-colors">
                  Accueil
                </a>
              </li>
              <li>
                <a href="#menu" className="text-gray-400 hover:text-[#3ABEFF] transition-colors">
                  Menu
                </a>
              </li>
              <li>
                <a href="#gallery" className="text-gray-400 hover:text-[#3ABEFF] transition-colors">
                  Galerie
                </a>
              </li>
              <li>
                <a href="#info" className="text-gray-400 hover:text-[#3ABEFF] transition-colors">
                  Infos
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 
              className="text-[#D4A85F] font-bold text-lg mb-4"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Contact
            </h3>
            <ul className="space-y-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {restaurantInfo.phones.map((phone, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <Phone className="text-[#3ABEFF] flex-shrink-0 mt-1" size={18} />
                  <a href={phone.link} className="text-gray-400 hover:text-[#3ABEFF] transition-colors">
                    {phone.number}
                  </a>
                </li>
              ))}
              <li className="flex items-start gap-3">
                <MapPin className="text-[#3ABEFF] flex-shrink-0 mt-1" size={18} />
                <span className="text-gray-400">{restaurantInfo.address}</span>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 
              className="text-[#D4A85F] font-bold text-lg mb-4"
              style={{ fontFamily: "'Crimson Text', serif" }}
            >
              Horaires
            </h3>
            <div className="flex items-start gap-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
              <Clock className="text-[#3ABEFF] flex-shrink-0 mt-1" size={18} />
              <div className="text-gray-400">
                <p>Lun-Ven: 11h – 00h</p>
                <p>Sam-Dim: 12h – 00h</p>
                <p className="text-[#D4A85F] font-semibold mt-2">{restaurantInfo.openingNote}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media & Copyright */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex gap-4">
              <a 
                href="#" 
                className="bg-gray-800 p-3 rounded-full hover:bg-[#D4A85F] hover:text-black transition-all duration-200"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
              <a 
                href="#" 
                className="bg-gray-800 p-3 rounded-full hover:bg-[#D4A85F] hover:text-black transition-all duration-200"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
            <p 
              className="text-gray-400 text-center"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              &copy; {currentYear} {restaurantInfo.name} | {' '}
              <button
                onClick={() => setIsLegalModalOpen(true)}
                className="hover:text-[#D4A85F] transition-colors underline"
              >
                Mentions légales
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Legal Modal */}
      <LegalModal isOpen={isLegalModalOpen} onClose={() => setIsLegalModalOpen(false)} />
    </footer>
  );
};
